// VB-021 — Canvas persistence service
// Handles load/save of the canvas snapshot (nodes + edges) to Supabase.
// Multi-brain: one snapshot per (user, canvas) — upsert strategy.

import { supabase } from '../lib/supabase';
import type { Node, Edge } from '@xyflow/react';
import type { CanvasNodeData } from '../types';
import { showToast } from '../utils/toast';

const CANVAS_SCHEMA_VERSION = 1;

export interface CanvasSnapshotPayload {
  nodes: Node<CanvasNodeData>[];
  edges: Edge[];
  schema_version: number;
}

export interface Brain {
  id: string;
  title: string;
  isDefault: boolean;
  createdAt: string | null;
}

export const canvasService = {
  /** Load a canvas snapshot from Supabase.
   *  If canvasId is provided, load that canvas. Otherwise, load the default brain. */
  async loadCanvas(userId: string, canvasId?: string): Promise<CanvasSnapshotPayload | null> {
    let targetCanvasId = canvasId;
    if (!targetCanvasId) {
      const { data: defaultBrain } = await supabase
        .from('visual_brain_canvases')
        .select('id')
        .eq('user_id', userId)
        .eq('is_default', true)
        .single();
      if (!defaultBrain) return null;
      targetCanvasId = defaultBrain.id;
    }
    const { data, error } = await supabase
      .from('canvas_snapshots')
      .select('nodes, edges, schema_version')
      .eq('user_id', userId)
      .eq('canvas_id', targetCanvasId)
      .maybeSingle();

    if (error) {
      console.error('[canvas.service] loadCanvas:', error.message);
      return null;
    }
    if (!data) return null;

    return {
      nodes: (data.nodes as unknown as Node<CanvasNodeData>[]) ?? [],
      edges: (data.edges as unknown as Edge[]) ?? [],
      schema_version: (data.schema_version as number) ?? CANVAS_SCHEMA_VERSION,
    };
  },

  /** Upsert the canvas snapshot for the current user + canvas.
   *  Safe to call repeatedly — uses ON CONFLICT (user_id, canvas_id) DO UPDATE. */
  async saveCanvas(
    userId: string,
    canvasId: string,
    nodes: Node<CanvasNodeData>[],
    edges: Edge[]
  ): Promise<void> {
    const { error } = await supabase.from('canvas_snapshots').upsert(
      {
        user_id: userId,
        canvas_id: canvasId,
        nodes: nodes as unknown as any,
        edges: edges as unknown as any,
        schema_version: CANVAS_SCHEMA_VERSION,
        saved_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,canvas_id' }
    );

    if (error) {
      console.error('[canvas.service] saveCanvas error:', error);
      showToast('Impossible de sauvegarder le canvas', 'error');
    }
  },

  async loadBrains(userId: string): Promise<Brain[]> {
    const { data, error } = await supabase
      .from('visual_brain_canvases')
      .select('id, title, is_default, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: true });
    if (error) {
      console.error('[canvas.service] loadBrains:', error.message);
      return [];
    }
    return (data || []).map((b) => ({
      id: b.id,
      title: b.title,
      isDefault: b.is_default,
      createdAt: b.created_at,
    }));
  },

  async createBrain(userId: string, title: string): Promise<Brain | null> {
    const { data, error } = await supabase
      .from('visual_brain_canvases')
      .insert({ title, user_id: userId, is_default: false })
      .select('id, title, is_default, created_at')
      .single();
    if (error) {
      console.error('[canvas.service] createBrain:', error.message);
      return null;
    }
    return {
      id: data.id,
      title: data.title,
      isDefault: data.is_default,
      createdAt: data.created_at,
    };
  },

  async renameBrain(brainId: string, newTitle: string): Promise<boolean> {
    const { error } = await supabase
      .from('visual_brain_canvases')
      .update({ title: newTitle, updated_at: new Date().toISOString() })
      .eq('id', brainId);
    if (error) {
      console.error('[canvas.service] renameBrain:', error.message);
      return false;
    }
    return true;
  },

  async deleteBrain(brainId: string): Promise<boolean> {
    await supabase.from('canvas_snapshots').delete().eq('canvas_id', brainId);
    const { error } = await supabase.from('visual_brain_canvases').delete().eq('id', brainId);
    if (error) {
      console.error('[canvas.service] deleteBrain:', error.message);
      return false;
    }
    return true;
  },
};
